//scheman/EditorWrapper.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useIsAuthorized, useUserInfo, useImpersonatedUserId, useLoggedInViaLink } from '../contexts/GlobalStateContext';
import { getUserInfoById, fetchWithAuth } from '../services/api';
import { getActiveUserNames, getSchemaDates } from '../services/UtilityServices';
import { toast } from 'react-toastify';
import ScheduleEditor from './ScheduleEditor';
import { getTodaysDate } from '../services/UtilityServices';
import './EditorWrapper.css';

const EditorWrapper = ({
  originalHtml,
  conflicts = [],
  isStandardUser,
  isEdited,
  currentDate,
  station,
  typingUsers = {},
  editLocks = {},
  myLocks = {},
  onStartTyping,
  onStopTyping,
  onRequestLock,
  onReleaseLock,
  onDocumentSaved,
  onNotifyDocumentSaved,
  selectDates,
  onDateChange,
  onEditModeChange,
  hideStatusBar = false,
  hideHeader
}) => {
  const { isAuthorized } = useIsAuthorized();
  const { userInfo } = useUserInfo();
  const { impersonatedUserId } = useImpersonatedUserId();
  const { loggedInViaLink } = useLoggedInViaLink();
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const isStandard = userInfo?.role === 'Standard';

  const [activeUserNames, setActiveUserNames] = useState([]);

  // Editor state
  const [originalHtmlDocument, setOriginalHtmlDocument] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [hasEditChanges, setHasEditChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bodyIsEdited, setBodyIsEdited] = useState(false);
  const [shouldBeRegenerated, setShouldBeRegenerated] = useState(false);
  const [fieldConflicts, setFieldConflicts] = useState([]);
  const [clientTimestamp, setClientTimestamp] = useState(Date.now());
  const trackedChanges = useRef({});
  const pendingPlaceradChanges = useRef([]);   // ← ny
  const editorRef = useRef(null);
  const documentSavedCallbackRef = useRef(null);

  const [selectedDay, setSelectedDay] = useState(currentDate || '');
  const [dailyReportNames, setDailyReportNames] = useState([]);
  const editDisabled = !!selectedDay && selectedDay < getTodaysDate();
  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  // ========== SYNC INCOMING PROPS ==========
  useEffect(() => {
    if (onEditModeChange) onEditModeChange(editMode);
  }, [editMode]);

  useEffect(() => {
    if (!isAuthorized || !originalHtml) return;
    setOriginalHtmlDocument(originalHtml);
    setFieldConflicts(conflicts || []);
  }, [originalHtml, isAuthorized, conflicts]);

  useEffect(() => {
    setBodyIsEdited(isEdited);
    if (isEdited) {
      const checkDiff = async () => { checkIfNumberOfUnitsDifferBetweenGeneratedAndEdited(); }
      checkDiff();
    }
  }, [isEdited]);

  // Reset vid datum/station-ändring
  useEffect(() => {
    setSelectedDay(currentDate || '');
  }, [currentDate]);

  useEffect(() => {
    if (!selectDates || !station) return;
    getDailyReportNames();
  }, [selectDates, station, impersonatedUserId]);

  useEffect(() => {
    setEditMode(false);
    setHasEditChanges(false);
    trackedChanges.current = {};
    pendingPlaceradChanges.current = [];
  }, [selectedDay, station]);

  // Frigör lås när edit mode stängs
  useEffect(() => {
    if (!editMode && myLocks && Object.keys(myLocks).length > 0 && onReleaseLock) {
      Object.keys(myLocks).forEach(entityId => {
        onReleaseLock(station, selectedDay, entityId);
      });
    }
  }, [editMode]);

  // ========== ACTIVE USER NAMES ==========
  useEffect(() => {
    if (isStandard) return;

    getActiveUserNames().then(setActiveUserNames);

    // Uppdatera var 60:e sekund
    const interval = setInterval(() => {
      getActiveUserNames().then(setActiveUserNames);
    }, 60000);

    return () => clearInterval(interval);
  }, [isStandard]);

  useEffect(() => {
    const loadData = async () => {
      if (!isAuthorized || !station) return;
      if (isSystemAdmin && impersonatedUserId) {
        const data = await getUserInfoById(impersonatedUserId);
        setImpersonatedUser(data);
      }
    };

    loadData();
  }, [isAuthorized, impersonatedUserId, station]);

  // ========== DOCUMENT SAVED BY OTHER ==========
  // Stabil forwarding-handler
  useEffect(() => {
    if (!onDocumentSaved) return;
    const handler = (data) => {
      if (documentSavedCallbackRef.current) {
        documentSavedCallbackRef.current(data);
      }
    };
    onDocumentSaved(handler);
    return () => { onDocumentSaved(null); };
  }, [onDocumentSaved]);

  // Uppdatera via ref (frisk closure)
  useEffect(() => {
    documentSavedCallbackRef.current = async (data) => {
      if (data.Station !== station || data.Date !== selectedDay) return;

      if (data.IsRegenerate) {
        if (hasEditChanges) {
          const reload = confirm(
            `${data.RegeneratedBy || data.SavedBy} återställde schemat.\n\n` +
            `Du har osparade ändringar som kommer försvinna.\n\n` +
            `Vill du ladda om?`
          );
          if (!reload) return;
        }
        setEditMode(false);
        setHasEditChanges(false);
        trackedChanges.current = {};
        pendingPlaceradChanges.current = [];
        await loadReport();
        toast.success('✅ Dokumentet har laddats om', { autoClose: 3000 });
      } else {
        await mergeChangesFromServer();
      }
    };
  }, [station, selectedDay, hasEditChanges]);

  const getDailyReportNames = async () => {
    try {
      const dates = await getSchemaDates(station, impersonatedUserId);
      if (dates.length === 0) {
        setDailyReportNames(['Underlag saknas']);
        setSelectedDay('');
      } else {
        setDailyReportNames(dates);
        const today = getTodaysDate();
        setSelectedDay(prev => prev || (dates.includes(today) ? today : dates[0]));
      }
    } catch (error) {
      console.error('Error fetching daily report names:', error);
      setDailyReportNames(['Underlag saknas']);
    }
  };

  const handleBlockedToggle = async (type, namn, blocked, itemStation) => {
    try {
      const stationToUse = itemStation || station;
      const dateToUse = currentDate || selectedDay;
      // station = visningsstation, itemStation = postens hemstation
      let url = `/api/editableschema/blocked?date=${dateToUse}&station=${encodeURIComponent(station)}&itemStation=${encodeURIComponent(stationToUse)}&type=${type}&namn=${encodeURIComponent(namn)}&blocked=${blocked}`;
      const response = await fetchWithAuth(url, { method: 'PUT' });
      if (response.ok) {
        await mergeChangesFromServer();
      } else {
        toast.error('Kunde inte uppdatera blockerad-status');
      }
    } catch (error) {
      console.error('Error toggling blocked:', error);
      toast.error('Fel vid uppdatering av blockerad-status');
    }
  };

  const handlePlaceradChange = (type, namn, placerad, itemStation) => {
    pendingPlaceradChanges.current = [
      ...pendingPlaceradChanges.current.filter(c => !(c.type === type && c.namn === namn)),
      { type, namn, placerad, station: itemStation || station }
    ];

    setHasEditChanges(true);
  };

  const handleDateChange = (e) => {
    if (editMode && hasEditChanges) {
      if (!confirm('Du har osparade ändringar. Vill du byta datum?')) return;
    }
    const newDate = e.target.value;
    setEditMode(false);
    setHasEditChanges(false);
    trackedChanges.current = {};
    pendingPlaceradChanges.current = [];

    // Uppdatera alltid selectedDay direkt så select inte hoppar tillbaka till
    // gammalt värde medan SignalR-svaret inväntas.
    setSelectedDay(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const checkIfNumberOfUnitsDifferBetweenGeneratedAndEdited = async () => {
    const dateToUse = currentDate || selectedDay;
    try {
      let url = `/api/editableschema/checkunitdiff?date=${dateToUse}&station=${encodeURIComponent(station)}&isStandardUser=${isStandardUser}`;
      if (impersonatedUserId) {
        url += `&userId=${impersonatedUserId}`;
      }
      const response = await fetchWithAuth(url);
      if (response.ok) {
        const identiska = await response.json();
        setShouldBeRegenerated(!identiska);
      }
    } catch (error) {
      console.error('Error checking unit diff:', error);
    }
  }

  // ========== LOAD REPORT ==========
  const loadReport = async () => {
    try {
      let metaUrl = `/api/editableschema/metadata?date=${selectedDay}&station=${encodeURIComponent(station)}&isStandardUser=${isStandardUser}`;
      if (impersonatedUserId) metaUrl += `&userId=${impersonatedUserId}`;
      const metaResponse = await fetchWithAuth(metaUrl);
      const metadata = await metaResponse.json();
      setBodyIsEdited(metadata.isEdited);

      let url = `/api/editableschema?date=${selectedDay}&station=${encodeURIComponent(station)}&isStandardUser=${isStandardUser}`;
      if (impersonatedUserId) url += `&userId=${impersonatedUserId}`;
      const response = await fetchWithAuth(url);
      if (response.ok) {
        const data = await response.json();
        setOriginalHtmlDocument(data.html);
        setFieldConflicts(data.conflicts || []);
        setClientTimestamp(Date.now());
      } else {
        toast.error('Kunde inte ladda schemat');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Fel vid laddning av schema');
    }
  };

  // ========== MERGE CHANGES FROM SERVER ==========
  const mergeChangesFromServer = async () => {
    try {
      let url = `/api/editableschema?date=${selectedDay}&station=${encodeURIComponent(station)}&isStandardUser=${isStandardUser}`;
      if (impersonatedUserId) url += `&userId=${impersonatedUserId}`;

      const response = await fetchWithAuth(url);
      if (response.ok) {
        const data = await response.json();
        const myChanges = { ...trackedChanges.current };
        setOriginalHtmlDocument(data.html);
        setFieldConflicts(data.conflicts || []);
        setClientTimestamp(Date.now());

        setTimeout(() => {
          if (editorRef.current?.contentRef?.current && Object.keys(myChanges).length > 0) {
            const contentRef = editorRef.current.contentRef.current;
            Object.entries(myChanges).forEach(([entityId, content]) => {
              const element = contentRef.querySelector(`[data-entity-id="${entityId}"]`);
              if (element) element.innerHTML = content;
            });
            toast.info('📥 Dokumentet uppdaterades', { autoClose: 3000 });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error merging changes:', error);
    }
  };

  // ========== CONTENT CHANGE TRACKING ==========
  const handleContentChange = (entityId = null) => {
    setHasEditChanges(true);
    if (entityId && editorRef.current?.contentRef?.current) {
      const element = editorRef.current.contentRef.current
        .querySelector(`[data-entity-id="${entityId}"]`);
      if (element) trackedChanges.current[entityId] = element.innerHTML;
    }
  };

  // ========== SAVE ==========
  const saveReport = async () => {
    if (!hasEditChanges) {
      setEditMode(false);
      return;
    }
    if (!editorRef.current) return;

    setSaving(true);
    try {
      // ── Spara Placerad till DB ──
      const dateToUse = currentDate || selectedDay;
      if (pendingPlaceradChanges.current.length > 0) {
        let placeradUrl = `/api/editableschema/placerad?date=${dateToUse}&station=${encodeURIComponent(station)}`;
        await fetchWithAuth(placeradUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pendingPlaceradChanges.current)
        });
        pendingPlaceradChanges.current = [];
      }

      const changesNow = {};
      if (editorRef.current.contentRef?.current) {
        const elements = editorRef.current.contentRef.current.querySelectorAll('[data-entity-id]');
        elements.forEach(el => {
          const entityId = el.dataset.entityId;
          if (myLocks?.[entityId]) {
            changesNow[entityId] = el.innerHTML;
          }
        });
      }

      const fieldChanges = Object.keys(trackedChanges.current).length > 0
        ? trackedChanges.current
        : changesNow;


      // Bara Blocked/Placerad-ändringar — ladda om från server
      if (Object.keys(fieldChanges).length === 0) {
        await loadReport();
        setEditMode(false);
        setHasEditChanges(false);
        trackedChanges.current = {};
        toast.success('Rapporten sparades!', { autoClose: 3000 });
        if (onNotifyDocumentSaved) {
          await onNotifyDocumentSaved(
            station,
            selectedDay,
            userInfo?.username ?? userInfo?.userName ?? 'Unknown'
          );
        }

        return;
      }

      // Fältändringar — merge
      let url = '/api/editableschema/merge';
      if (impersonatedUserId) url += `?userId=${impersonatedUserId}`;

      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDay,
          station,
          fieldChanges,
          clientTimestamp,
          originalHtml: originalHtmlDocument
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.conflicts?.length > 0) {
          toast.warning(`Sparades med automatiska ihopslagningar: ${result.conflicts.join(', ')}`);
        } else {
          toast.success('Rapporten sparades!', { autoClose: 3000 });
        }
        setFieldConflicts(result.conflicts);
        setOriginalHtmlDocument(result.mergedHtml);
        setClientTimestamp(result.serverTimestamp);
        setEditMode(false);
        setBodyIsEdited(true);
        setHasEditChanges(false);
        trackedChanges.current = {};
        pendingPlaceradChanges.current = [];
        // ✅ Notifiera andra via SignalR
        if (onNotifyDocumentSaved) {
          await onNotifyDocumentSaved(
            station,
            selectedDay,
            userInfo?.username ?? userInfo?.userName ?? 'Unknown'
          );
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Server error:', response.status, errorText);
        toast.error('Kunde inte spara schemat');
      }
    } catch (error) {
      console.error('❌ Error saving DETAILS:', error, error?.message, error?.stack);
      toast.error('Fel vid sparning');
    } finally {
      setSaving(false);
    }
  };

  // ========== CANCEL ==========
  const cancelEdit = async () => {
    if (hasEditChanges && !confirm('Du har osparade ändringar. Vill du avbryta?')) return;
    await loadReport();
    setEditMode(false);
    setHasEditChanges(false);
    trackedChanges.current = {};
    pendingPlaceradChanges.current = [];
  };

  // ========== REGENERATE ==========
  const regenerateReport = async () => {
    if (!confirm('Vill du återskapa schemat från källdata? Alla ändringar försvinner.')) return;
    try {
      let url = `/api/editableschema?date=${selectedDay}&station=${encodeURIComponent(station)}&isStandardUser=${isStandardUser}`;
      if (impersonatedUserId) url += `&userId=${impersonatedUserId}`;
      await fetchWithAuth(url, { method: 'DELETE' });
      await loadReport();
      toast.success('Schemat återställdes', { autoClose: 3000 });
    } catch (error) {
      console.error('Error regenerating:', error);
      toast.error('Fel vid återställning');
    }
  };

  // ========== RENDER ==========
  if (!isAuthorized) {
    return (
      <div className="tw-container tw-mx-auto">
        <div className="tw-p-1" />
        <div className="tw-mb-2 tw-w-full">
          <div className="card rounded-3 overflow-hidden border" style={{ width: '1078px', maxWidth: '100%' }}>
            <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
              <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Logga in för att se scheman</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Status-rad */}
      {!hideStatusBar && (
      <div className="tw-relative tw-flex tw-items-center tw-px-4 tw-py-1 tw-min-h-[36px]">

        {/* 👅 Vänster — online-användare */}
        {activeUserNames.length > 0 && (
          <span className="tw-text-sm tw-text-gray-700 tw-flex-shrink-0">
            👥 Online: {activeUserNames.join(', ')}
          </span>
        )}

        {!loggedInViaLink && selectDates && dailyReportNames.length > 0 && (
          <div className="tw-fixed tw-left-[17cm] tw-flex tw-items-center tw-gap-2">
            <label htmlFor="date-select" className="tw-text-sm tw-font-medium tw-text-gray-700">
              Välj datum:
            </label>
            <select
              id="date-select"
              value={selectedDay}
              onChange={handleDateChange}
              disabled={dailyReportNames[0] === 'Underlag saknas' || editMode}
              className="tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            >
              <option value="">-- Välj datum --</option>
              {dailyReportNames.map((date, index) => (
                <option key={index} value={date} disabled={date === 'Underlag saknas'}>
                  {date}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 👅 Center — typing, locks, knappar, status */}
        <div className="tw-fixed tw-left-[30cm] tw-flex tw-items-center tw-gap-4">
          {Object.values(typingUsers).filter(Boolean).map(user => (
            <span key={user.fieldId} className="tw-text-sm tw-text-blue-600 tw-animate-pulse">
              ✏️ {user.userName} skriver...
            </span>
          ))}

          {Object.entries(editLocks).map(([entityId, lock]) => (
            <span key={entityId} className="tw-text-sm tw-text-yellow-700">
              🔒 {lock.lockedBy} redigerar {entityId}
            </span>
          ))}

          {impersonatedUserId && impersonatedUser && (
            <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
              Du agerar som: <strong className="tw-text-red-500">{impersonatedUser.userName}</strong>
            </span>
          )}

          {!isStandard && (
            <>
              {!editMode ? (
                <>
                  <button
                    disabled={editDisabled}
                    className="editable-html-button btn-edit"
                    onClick={() => setEditMode(true)}
                  >
                    ✏️ Redigera
                  </button>
                  {bodyIsEdited && (
                    <button
                      disabled={editDisabled}
                      className="editable-html-button btn-regenerate"
                      onClick={regenerateReport}
                    >
                      🔄 Återställ
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    className="editable-html-button btn-save"
                    onClick={saveReport}
                    disabled={saving}
                  >
                    {saving ? '💾 Sparar...' : '💾 Spara'}
                  </button>
                  <button
                    className="editable-html-button btn-cancel"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    ❌ Avbryt
                  </button>
                </>
              )}
            </>
          )}

          {bodyIsEdited && <span className="editable-badge">✓ Editerad</span>}
          {bodyIsEdited && shouldBeRegenerated && <span className="unsaved-badge">⚠ Olika antal enheter i importerad vs. editerad</span>}
          {hasEditChanges && <span className="unsaved-badge">⚠ Osparad</span>}
          {editMode && <span className="tw-text-sm tw-text-amber-600 tw-font-medium">🔒 Avsluta redigering för att navigera</span>}
        </div>
      </div>
      )}

      {/* ScheduleEditor direkt — ingen ScheduleEditorWrapper */}
      <ScheduleEditor
        ref={editorRef}
        originalHtml={originalHtmlDocument}
        editMode={editMode}
        onContentChange={handleContentChange}
        fieldConflicts={fieldConflicts}
        station={station}
        currentDate={selectedDay}
        typingUsers={typingUsers}
        editLocks={editLocks}
        myLocks={myLocks}
        onStartTyping={onStartTyping}
        onStopTyping={onStopTyping}
        onRequestLock={onRequestLock}
        onReleaseLock={onReleaseLock}
        onBlockedToggle={handleBlockedToggle}
        onPlaceradChange={handlePlaceradChange}
        hideHeader={hideHeader}
      />
    </>
  );
};

export default EditorWrapper;